import { useMemo } from "react";
import { ArrowLeft, ChevronRight, LogOut, User } from "lucide-react";
import { useNavigate, useLocation } from "react-router";
import {
  getPageTitle,
  getClientConfigurationTabLabel,
  isClientConfigurationPath,
} from "../../utils/pageTitles";
import { redirectToSsoLogin } from "../../SSO/ssoConfig";
import { formatAuthUserHeaderLabel, resolveLoggedInUserRoleDescription, resolveLoggedInUserRoleLabel } from "../../utils/authUserDisplay";
import {
  clearAuth,
  selectAuth,
  useAppDispatch,
  useAppSelector,
  useGetAssignableEmployeesQuery,
  useGetAuthProfileQuery,
  useGetClientConfigurationEmployeesQuery,
  useLogoutMutation,
} from "../../store";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { cn } from "../ui/utils";

export const Header = () => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector(selectAuth);
  const [logout] = useLogoutMutation();
  const { data: authProfile } = useGetAuthProfileQuery(undefined, {
    skip: !auth.accessToken,
  });
  const { data: assignableEmployees = [] } = useGetAssignableEmployeesQuery(undefined, {
    skip: !auth.accessToken,
  });
  const { data: employees = [] } = useGetClientConfigurationEmployeesQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const title = getPageTitle(location.pathname, location.search);
  const showClientTabCrumb = isClientConfigurationPath(location.pathname);
  const clientTabLabel = showClientTabCrumb
    ? getClientConfigurationTabLabel(
        new URLSearchParams(location.search).get("tab")
      )
    : null;

  const isDashboardHome = location.pathname === "/dashboard";
  const canGoBack =
    !isDashboardHome &&
    typeof window !== "undefined" &&
    window.history.length > 1;

  const handleBack = () => {
    if (canGoBack) navigate(-1);
  };

  const handleSignOut = async () => {
    if (auth.accessToken) {
      try {
        await logout().unwrap();
      } catch {
        // Still clear local auth if the server is unreachable or the session was already ended.
      }
    }
    dispatch(clearAuth());
    if (!redirectToSsoLogin()) {
      navigate("/", { replace: true });
    }
  };

  const displayName = useMemo(
    () => formatAuthUserHeaderLabel(auth, employees),
    [auth, employees]
  );

  const roleLabel = useMemo(
    () =>
      resolveLoggedInUserRoleLabel(auth, {
        authProfile,
        assignableEmployees,
      }),
    [auth, authProfile, assignableEmployees]
  );

  const roleDescription = useMemo(
    () =>
      resolveLoggedInUserRoleDescription(auth, {
        authProfile,
        assignableEmployees,
      }),
    [auth, authProfile, assignableEmployees]
  );

  const showRoleDescriptionTooltip =
    Boolean(roleDescription) &&
    roleDescription.toLowerCase() !== roleLabel.toLowerCase();

  const roleLineClassName =
    "max-w-[14rem] truncate text-xs text-blue-100/75 md:max-w-[18rem]";

  const roleLine = roleLabel ? (
    showRoleDescriptionTooltip ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              roleLineClassName,
              "cursor-help underline decoration-dotted decoration-blue-100/45 underline-offset-2"
            )}
            aria-label={`${roleLabel}; hover for role description`}
          >
            {roleLabel}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          sideOffset={6}
          className="max-w-xs text-balance"
        >
          {roleDescription}
        </TooltipContent>
      </Tooltip>
    ) : (
      <div className={roleLineClassName} title={roleLabel}>
        {roleLabel}
      </div>
    )
  ) : null;

  return (
    <header className="header-shell">
      <div aria-hidden="true" className="header-inner-glow" />
      <div aria-hidden="true" className="header-edge-top" />
      <div aria-hidden="true" className="header-junction-glow" />
      <div aria-hidden="true" className="header-junction-line" />

      <nav className="header-content" aria-label="Breadcrumb">
        <button
          type="button"
          onClick={handleBack}
          disabled={!canGoBack}
          className={cn(
            "header-back-btn",
            canGoBack ? "header-back-btn-enabled" : "header-back-btn-disabled"
          )}
          aria-label="Go back to previous page"
        >
          <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
          <span>Back</span>
        </button>

        <ChevronRight
          className="h-4 w-4 shrink-0 text-blue-200/55"
          strokeWidth={2.25}
          aria-hidden
        />

        <span className="header-title">{title}</span>

        {clientTabLabel ? (
          <>
            <ChevronRight
              className="h-4 w-4 shrink-0 text-blue-200/55"
              strokeWidth={2.25}
              aria-hidden
            />
            <span className="header-subtitle">{clientTabLabel}</span>
          </>
        ) : null}
      </nav>

      <div className="header-user-rail">
        <div className="flex items-center gap-2">
          <div className="header-user-avatar">
            <User className="h-4 w-4 text-blue-100" aria-hidden />
          </div>
          <div className="hidden min-w-0 sm:block">
            <div
              className="max-w-[14rem] truncate text-sm font-medium text-white md:max-w-[18rem]"
              title={displayName}
            >
              {displayName}
            </div>
            {roleLine}
          </div>
        </div>
        <button
          type="button"
          onClick={handleSignOut}
          className="header-sign-out-btn"
          aria-label="Sign out"
        >
          <LogOut className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>
    </header>
  );
};
